import React, { useEffect, useState, useCallback } from 'react';
import { LogOut, Layers, FileText, Tv, Link2, Plus, ChevronDown } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useCategories } from '../context/CategoriesContext';
import AboutEditor from './AboutEditor';
import ChannelEditor from './ChannelEditor';
import ContactEditor from './ContactEditor';
import AdminMediaList from './AdminMediaList';
import UploadForm from './UploadForm';

export default function Admin() {
  const { user, signOut } = useAuth();
  const { categories } = useCategories();
  const CATEGORIES = categories.map((c) => ({ key: c.key, label: c.name }));
  const [section, setSection] = useState('media'); // 'media' | 'channels' | 'about'
  const [items, setItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);

  const reload = useCallback(async () => {
    const { data } = await api.get('/media');
    setItems(data);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Keep the selected channel valid as channels are added/removed/loaded.
  useEffect(() => {
    const keys = categories.map((c) => c.key);
    if (!keys.length) return;
    if (!keys.includes(activeCategory)) setActiveCategory(keys[0]);
  }, [categories, activeCategory]);

  const filtered = items.filter((it) => it.category === activeCategory);

  return (
    <div className="page-in max-w-7xl mx-auto px-4 sm:px-6 py-5 md:py-10" data-testid="admin-page">
      <div className="flex items-start justify-between gap-4 border-b border-[#3a2512] pb-4 md:pb-6 mb-4 md:mb-6">
        <div className="min-w-0">
          <div className="font-osd text-[#f5a623] text-[11px] sm:text-xs tracking-widest mb-1">// CONTROL ROOM · LIVE</div>
          <h1 className="font-display text-2xl sm:text-4xl md:text-5xl text-[#ffe9b8] phosphor-glow leading-tight">ADMIN BROADCAST DESK</h1>
          <p className="font-body text-[#a88552] text-xs sm:text-sm mt-2 break-words">
            Logged in as <span className="text-[#ffc968]">{user?.email}</span>
            <span className="hidden sm:inline">. Upload <span className="text-[#d8b887]">.mp4 / .mov</span>, <span className="text-[#d8b887]">.jpg / .png / .webp</span> or <span className="text-[#d8b887]">.glb / .gltf</span> files.</span>
          </p>
        </div>
        <button onClick={signOut} data-testid="admin-logout-btn" aria-label="Sign out"
                className="admin-action shrink-0 font-osd text-xs tracking-widest h-11 px-3 border-2 border-[#5a3418] text-[#d8b887] hover:border-[#f5a623] hover:text-[#ffc968] flex items-center gap-2 rounded-md">
          <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">SIGN OUT</span>
        </button>
      </div>

      {/* Section tabs — sticky on scroll so they're always reachable on phones */}
      <nav aria-label="Admin sections" className="sticky top-0 z-30 -mx-4 px-4 py-2.5 mb-5 bg-[#0c0603]/95 backdrop-blur-md border-b border-[#2a1608] grid grid-cols-2 gap-2 sm:static sm:mx-0 sm:px-0 sm:py-0 sm:mb-8 sm:bg-transparent sm:backdrop-blur-none sm:border-0 sm:flex sm:flex-wrap">
        <SectionTab active={section === 'media'} onClick={() => setSection('media')} icon={Layers} testId="admin-section-media">BROADCASTS</SectionTab>
        <SectionTab active={section === 'channels'} onClick={() => setSection('channels')} icon={Tv} testId="admin-section-channels">CHANNELS</SectionTab>
        <SectionTab active={section === 'about'} onClick={() => setSection('about')} icon={FileText} testId="admin-section-about">ABOUT PAGE</SectionTab>
        <SectionTab active={section === 'contact'} onClick={() => setSection('contact')} icon={Link2} testId="admin-section-contact">SITE & SYNC</SectionTab>
      </nav>

      {section === 'about' ? <AboutEditor /> : section === 'contact' ? <ContactEditor /> : section === 'channels' ? <ChannelEditor /> : (
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-6 lg:gap-8">
        <div className="min-w-0">
        <button type="button" onClick={() => setUploadOpen((v) => !v)} aria-expanded={uploadOpen} data-testid="admin-upload-toggle"
                className="admin-action lg:hidden w-full h-12 flex items-center justify-between gap-2 px-4 border-2 border-[#f5a623] text-[#ffc968] bg-[#1a0e05] rounded-lg font-osd text-sm tracking-widest">
          <span className="flex items-center gap-2"><Plus className="w-4 h-4" /> NEW BROADCAST</span>
          <ChevronDown className={`w-5 h-5 transition-transform ${uploadOpen ? 'rotate-180' : ''}`} />
        </button>
        <UploadForm categories={CATEGORIES} className={`${uploadOpen ? 'block mt-3' : 'hidden'} lg:block lg:mt-0`}
                    onUploaded={(cat) => { setActiveCategory(cat); reload(); }} />
        </div>

        {/* Items list */}
        <div className="min-w-0">
          <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar snap-x -mx-4 px-4 pb-1 sm:mx-0 sm:px-0 sm:flex-wrap sm:overflow-visible" role="tablist" aria-label="Channels">
            {CATEGORIES.map((c) => {
              const active = c.key === activeCategory;
              const count = items.filter((it) => it.category === c.key).length;
              return (
                <button
                  key={c.key}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveCategory(c.key)}
                  data-testid={`admin-tab-${c.key}`}
                  className={`admin-action snap-start shrink-0 h-10 font-osd text-xs tracking-widest px-3 border-2 rounded-md whitespace-nowrap transition-colors ${active ? 'bg-[#f5a623] text-[#1a0e05] border-[#1a0e05]' : 'border-[#5a3418] text-[#d8b887] hover:border-[#f5a623] hover:text-[#ffc968] bg-[#1a0e05]/40'}`}
                >
                  {c.label} <span className="opacity-70 ml-1">{count}</span>
                </button>
              );
            })}
          </div>

          <AdminMediaList items={filtered} category={activeCategory} onChanged={reload} />
        </div>
      </div>
      )}
    </div>
  );
}

function SectionTab({ active, onClick, icon: Icon, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      aria-current={active ? 'page' : undefined}
      className={`admin-action flex items-center justify-center gap-2 font-osd text-xs tracking-widest h-11 px-3 sm:px-4 border-2 rounded-md transition-colors ${active ? 'bg-[#f5a623] text-[#1a0e05] border-[#1a0e05] shadow-[2px_2px_0_#1a0e05]' : 'border-[#5a3418] text-[#d8b887] hover:border-[#f5a623] hover:text-[#ffc968]'}`}
    >
      <Icon className="w-3.5 h-3.5" /> {children}
    </button>
  );
}
